import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import { token } from '@atlaskit/tokens';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { DriverField } from '@atlassian/jira-software-releases-version-driver-field/src/ui/index.tsx';
import { VersionSummaryDescription } from '@atlassian/jira-software-releases-version-summary-description/src/ui/index.tsx';
import { FIRST_COLUMN_MIN_WIDTH } from '../common/constants.tsx';
import { NonCriticalErrorBoundary } from '../common/utils/non-critical-error-boundary/NonCriticalErrorBoundary.tsx';
import { LaunchCardExperienceSuccess } from '../services/experience-tracker/LaunchCardExperienceStart.tsx';
import type { ui_softwareReleasesLaunchCard_LaunchCard$key } from './__generated__/ui_softwareReleasesLaunchCard_LaunchCard.graphql';
import LatestDeploymentForVersionPreconditionQuery, {
	type latestDeploymentForVersionPreconditionQuery,
} from './latest-deployment/__generated__/latestDeploymentForVersionPreconditionQuery.graphql';
import VersionContributorsQuery, {
	type async_softwareReleasesVersionContributorsQuery,
} from './version-contributors/__generated__/async_softwareReleasesVersionContributorsQuery.graphql';
import { LatestDeployment } from './latest-deployment/index.tsx';
import {
	AsyncVersionContributors as VersionContributors,
	VersionContributorsSectionSkeleton,
} from './version-contributors/async.tsx';
import { VersionDates } from './version-dates/index.tsx';
import { AsyncVersionStatus as VersionStatus } from './version-status/async.tsx';

export type Props = {
	jiraVersion: ui_softwareReleasesLaunchCard_LaunchCard$key;
};

export const LaunchCard = ({ jiraVersion }: Props): React.JSX.Element => {
	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesLaunchCard_LaunchCard on JiraVersion {
				...latestDeployment_softwareReleasesLaunchCard_jiraVersion
					@skip(if: $isDevopsFeatureDisabledInFedRamp)
				...versionDates_softwareReleasesLaunchCard
				...async_softwareReleasesLaunchCard_AsyncVersionStatus
				...ui_softwareReleasesVersionDriverField_DriverField
				...ui_softwareReleasesVersionSummaryDescription_VersionSummaryDescription

				id @required(action: THROW)

				driverData @optIn(to: ["JiraVersionDriverResult"]) {
					driver {
						id
					}
				}
				canEdit @required(action: THROW)
				project @required(action: THROW) {
					cloudId @required(action: THROW)
					key @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const [contributorsQueryRef, loadContributorsQuery] =
		useQueryLoader<async_softwareReleasesVersionContributorsQuery>(VersionContributorsQuery);

	const [preconditionQueryRef, loadPreconditionQuery] =
		useQueryLoader<latestDeploymentForVersionPreconditionQuery>(
			LatestDeploymentForVersionPreconditionQuery,
		);

	useEffect(() => {
		if (!isDevopsFeatureDisabledInFedRamp()) {
			loadContributorsQuery({ versionId: version.id });
			loadPreconditionQuery({ cloudId: version.project.cloudId, projectKey: version.project.key });
		}
	}, [
		loadPreconditionQuery,
		loadContributorsQuery,
		version.project.cloudId,
		version.project.key,
		version.id,
	]);

	const hasDriver = version.driverData?.driver?.id;

	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="launchCard">
			<FireScreenAnalytics
				attributes={{
					hasDriver: Boolean(hasDriver),
					versionAri: version.id,
				}}
			/>
			<BaseWrapper>
				<LaunchCardExperienceSuccess />
				<MainContentWrapper>
					<VersionStatus jiraVersion={version} />
					<VersionDates jiraVersion={version} />
					<UserFieldsContainer>
						<FieldWrapper>
							<NonCriticalErrorBoundary eventLocation="launchCardDriverField" fallback="flag">
								<DriverField
									id="jira-version-driver-field"
									jiraVersion={version}
									isDisabled={!version.canEdit}
								/>
							</NonCriticalErrorBoundary>
						</FieldWrapper>
						{!isDevopsFeatureDisabledInFedRamp() && contributorsQueryRef && (
							<NonCriticalErrorBoundary eventLocation="launchCardContributorsField" fallback="flag">
								<Placeholder
									name="version-contributors-section"
									fallback={<VersionContributorsSectionSkeleton />}
								>
									<VersionContributors queryRef={contributorsQueryRef} />
								</Placeholder>
							</NonCriticalErrorBoundary>
						)}
					</UserFieldsContainer>
					<VersionSummaryDescription jiraVersion={version} />
				</MainContentWrapper>
				{!isDevopsFeatureDisabledInFedRamp() && preconditionQueryRef && (
					<NonCriticalErrorBoundary eventLocation="launchCardLatestDeployment" fallback="unmount">
						<Placeholder name="latest-deployment" fallback={null}>
							<LatestDeployment jiraVersion={version} preconditionQueryRef={preconditionQueryRef} />
						</Placeholder>
					</NonCriticalErrorBoundary>
				)}
			</BaseWrapper>
		</ContextualAnalyticsData>
	);
};

/**
 * Loading component ends
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BaseWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	minHeight: '228px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const MainContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	paddingTop: token('space.300'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
	gap: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserFieldsContainer = styled.div({
	display: 'flex',
	gap: token('space.100'),
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled.div({
	flex: '1',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minWidth: `${FIRST_COLUMN_MIN_WIDTH}px`,
	},
});
